import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { formatDuration, formatDecimalHours, todayInPlant } from '../../shared/time.ts';
import { policz } from '../../shared/pl.ts';
import { Blad, Kpi, Ladowanie, Odznaka, Pusto, Tabela, Zakladki, type Kolumna, type Ton } from '../ui/index.tsx';

/**
 * Pracownicy — lista i centrum pojedynczej osoby.
 *
 * Świadomie NIE jest to ranking. Sortowanie domyślne idzie po loginie, a nie po
 * efektywności: system ma pomagać zarządzać pracą, a nie codziennie ustawiać
 * ludzi w kolejce od najgorszego. Porównanie ze średnią jest dostępne, ale jako
 * kontekst przy konkretnej osobie, nie jako tabela wyników na wejściu.
 */

interface Agregat {
  creditMin: number; availableMin: number; registeredMin: number; gapMin: number;
  efficiency: number | null; days: number;
}
interface Osoba {
  id: number; login: string; zone: string | null; station_code: string | null;
  total: Agregat; wpisow: number; sztuk: number; zlecen: number; brakWpisow: boolean;
}
interface Lista {
  od: string; do: string; pracownicy: Osoba[];
  srednia: { efficiency: number; wpisow: number; sztuk: number } | null;
}

const proc = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`);
const ton = (v: number | null): Ton => (v === null ? 'neutral' : v >= 0.9 ? 'ok' : v >= 0.7 ? 'warn' : 'bad');
const klasa = (v: number | null) =>
  v === null ? 'text-plum-muted' : v >= 0.9 ? 'text-ok' : v >= 0.7 ? 'text-warn' : 'text-bad';

export default function Pracownicy() {
  const { id } = useParams();
  return id ? <Szczegol id={Number(id)} /> : <ListaLudzi />;
}

// ---------------------------------------------------------------- lista

function ListaLudzi() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const od = params.get('od') ?? `${todayInPlant().slice(0, 7)}-01`;
  const doD = params.get('do') ?? todayInPlant();

  const [d, setD] = useState<Lista | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [laduje, setLaduje] = useState(true);

  const load = () => {
    setLaduje(true); setBlad(null);
    api.get<Lista>(`/pracownicy?od=${od}&do=${doD}`)
      .then(setD).catch((e) => setBlad(e.message)).finally(() => setLaduje(false));
  };
  useEffect(load, [od, doD]);

  const ustaw = (k: string, v: string) => {
    const n = new URLSearchParams(params); n.set(k, v); setParams(n, { replace: true });
  };

  if (blad) return <Blad tresc={blad} ponow={load} />;

  const zPraca = d?.pracownicy.filter((p) => !p.brakWpisow) ?? [];
  const bez = d?.pracownicy.filter((p) => p.brakWpisow) ?? [];

  const kolumny: Array<Kolumna<Osoba>> = [
    { klucz: 'login', naglowek: 'Login', sort: (p) => p.login, szer: '110px',
      render: (p) => <span className="font-semibold tnum text-plum">{p.login}</span> },
    { klucz: 'strefa', naglowek: 'Strefa', sort: (p) => p.zone ?? '',
      render: (p) => <span className="text-plum-muted">{p.zone ?? '—'}</span> },
    { klucz: 'dni', naglowek: 'Dni', sort: (p) => p.total.days, prawo: true, szer: '70px',
      render: (p) => <span className="tnum">{p.total.days}</span> },
    { klucz: 'wpisy', naglowek: 'Wpisy', sort: (p) => p.wpisow, prawo: true, szer: '80px',
      render: (p) => <span className="tnum">{p.wpisow}</span> },
    { klucz: 'sztuki', naglowek: 'Sztuki', sort: (p) => p.sztuk, prawo: true, szer: '80px',
      render: (p) => <span className="tnum">{p.sztuk}</span> },
    { klucz: 'norma', naglowek: 'Norma', sort: (p) => p.total.creditMin, prawo: true, szer: '96px',
      render: (p) => <span className="tnum">{formatDuration(p.total.creditMin)}</span> },
    { klucz: 'luka', naglowek: 'Luka', sort: (p) => p.total.gapMin, prawo: true, szer: '90px',
      render: (p) => (
        <span className={`tnum ${p.total.gapMin > 60 ? 'text-bad' : 'text-plum-muted'}`}>
          {formatDuration(Math.max(0, p.total.gapMin))}
        </span>
      ) },
    { klucz: 'ef', naglowek: 'Efektywność', sort: (p) => p.total.efficiency ?? -1, prawo: true, szer: '116px',
      render: (p) => <span className={`tnum font-semibold ${klasa(p.total.efficiency)}`}>{proc(p.total.efficiency)}</span> },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-head-sm">Pracownicy</h1>
        <span className="text-body-sm text-plum-muted">
          {policz(zPraca.length, 'pracownik')} z zarejestrowaną pracą
        </span>
      </div>

      <div className="card-flat p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label" htmlFor="od">Od</label>
          <input id="od" type="date" className="field tnum !w-auto" value={od} onChange={(e) => ustaw('od', e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="do">Do</label>
          <input id="do" type="date" className="field tnum !w-auto" value={doD} max={todayInPlant()} onChange={(e) => ustaw('do', e.target.value)} />
        </div>
        {d?.srednia && (
          <p className="text-body-sm text-plum-muted ml-auto">
            Średnia zakładu: <b className={klasa(d.srednia.efficiency)}>{proc(d.srednia.efficiency)}</b>
            {' · '}{d.srednia.wpisow.toFixed(0)} wpisów i {d.srednia.sztuk.toFixed(0)} sztuk na osobę
          </p>
        )}
      </div>

      <Tabela
        dane={zPraca}
        kolumny={kolumny}
        klucz={(p) => p.id}
        laduje={laduje}
        onKlik={(p) => nav(`/pracownicy/${p.id}?od=${od}&do=${doD}`)}
        pusto={<Pusto tytul="Nikt nie zarejestrował pracy" opis="W wybranym zakresie dat nie ma żadnych wpisów." />}
      />

      {bez.length > 0 && (
        <section className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-line">
            <h2 className="font-semibold text-plum">Bez wpisów — {policz(bez.length, 'pracownik')}</h2>
            <p className="mt-1 text-body-sm text-plum-muted">
              Celowo <b>nie</b> pokazujemy im 0% efektywności. Brak wpisów najczęściej znaczy urlop,
              zwolnienie albo delegację, a tego aplikacja jeszcze nie wie — zero byłoby oskarżeniem
              bez dowodu.
            </p>
          </div>
          <div className="p-4 flex flex-wrap gap-2">
            {bez.map((p) => (
              <button key={p.id} className="badge-neutral tnum hover:border-line-strong"
                      onClick={() => nav(`/pracownicy/${p.id}?od=${od}&do=${doD}`)}>
                {p.login}
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- centrum osoby

interface Dzien {
  work_date: string; registeredMin: number; availableMin: number; creditMin: number;
  gapMin: number; efficiency: number | null; entryCount: number; missingStandard: string[];
}
interface Miesiac { miesiac: string; nazwa: string; total: Agregat; dni: Dzien[] }
interface Detal {
  pracownik: { id: number; login: string; zone: string | null; station_code: string | null };
  od: string; do: string; total: Agregat;
  wpisow: number; sztuk: number; zlecen: number;
  stanowiska: Array<{ kod: string; nazwa: string; ile: number }>;
  miesiace: Miesiac[];
}

function Szczegol({ id }: { id: number }) {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const rok = todayInPlant().slice(0, 4);
  const od = params.get('od') ?? `${rok}-01-01`;
  const doD = params.get('do') ?? todayInPlant();

  const [d, setD] = useState<Detal | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [otwartyMiesiac, setOtwarty] = useState<string | null>(null);
  const [widok, setWidok] = useState<'historia' | 'stanowiska'>('historia');

  const load = () => {
    setBlad(null);
    api.get<Detal>(`/pracownicy/${id}?od=${od}&do=${doD}`).then((r) => {
      setD(r);
      setOtwarty((m) => m ?? r.miesiace[0]?.miesiac ?? null);
    }).catch((e) => setBlad(e.message));
  };
  useEffect(load, [id, od, doD]);

  if (blad) return <Blad tresc={blad} ponow={load} />;
  if (!d) return <Ladowanie />;

  const ustaw = (k: string, v: string) => {
    const n = new URLSearchParams(params); n.set(k, v); setParams(n, { replace: true });
  };

  return (
    <div className="space-y-4">
      <button className="btn-quiet -ml-3" onClick={() => nav(`/pracownicy?od=${od}&do=${doD}`)}>
        ← Wszyscy pracownicy
      </button>

      <header className="card p-5">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <h1 className="text-head font-bold text-plum tnum">{d.pracownik.login}</h1>
          {d.pracownik.zone && <Odznaka>strefa {d.pracownik.zone}</Odznaka>}
          <div className="ml-auto flex flex-wrap items-end gap-3">
            <div>
              <label className="label" htmlFor="od">Od</label>
              <input id="od" type="date" className="field tnum !w-auto" value={od} onChange={(e) => ustaw('od', e.target.value)} />
            </div>
            <div>
              <label className="label" htmlFor="do">Do</label>
              <input id="do" type="date" className="field tnum !w-auto" value={doD} max={todayInPlant()} onChange={(e) => ustaw('do', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
          <Kpi etykieta="Efektywność" wartosc={proc(d.total.efficiency)} ton={ton(d.total.efficiency)}
               udzial={d.total.efficiency ?? 0} pod={`${policz(d.total.days, 'dzien')} z pracą`} />
          <Kpi etykieta="Wypracowana norma" wartosc={formatDuration(d.total.creditMin)}
               pod={formatDecimalHours(d.total.creditMin)} />
          <Kpi etykieta="Czas dostępny" wartosc={formatDuration(d.total.availableMin)}
               pod={formatDecimalHours(d.total.availableMin)} />
          <Kpi etykieta="Sztuki" wartosc={d.sztuk} pod={`${policz(d.zlecen, 'zlecenie')}`} />
          <Kpi etykieta="Nierozpisany czas" wartosc={formatDuration(Math.max(0, d.total.gapMin))}
               ton={d.total.gapMin > 120 ? 'bad' : 'neutral'}
               pod={`${policz(d.wpisow, 'wpis')} łącznie`} />
        </div>
      </header>

      <Zakladki wartosc={widok} onZmiana={setWidok}
                opcje={[['historia', 'Historia'], ['stanowiska', 'Gdzie pracował']]} />

      {widok === 'stanowiska' && (
        <div className="card p-5">
          <h2 className="font-semibold text-plum mb-3">Rozkład pracy po stanowiskach</h2>
          {d.stanowiska.length === 0 && <p className="text-body-sm text-plum-muted">Brak wpisów w tym okresie.</p>}
          <div className="space-y-2">
            {d.stanowiska.map((s) => (
              <div key={s.kod} className="flex items-center gap-3">
                <span className="w-44 shrink-0 text-body-sm">{s.nazwa}</span>
                <div className="meter flex-1">
                  <span className="bg-plum" style={{ width: `${(s.ile / d.wpisow) * 100}%` }} />
                </div>
                <span className="w-24 text-right text-caption tnum text-plum-muted">
                  {policz(s.ile, 'wpis')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {widok === 'historia' && (
        <div className="space-y-3">
          {d.miesiace.length === 0 && (
            <div className="card"><Pusto tytul="Brak pracy w tym okresie" opis="Zmień zakres dat, żeby zobaczyć wcześniejsze miesiące." /></div>
          )}
          {d.miesiace.map((m) => {
            const otwarty = otwartyMiesiac === m.miesiac;
            return (
              <section key={m.miesiac} className="card overflow-hidden">
                <button
                  className="w-full px-5 py-4 flex flex-wrap items-center gap-4 text-left hover:bg-canvas transition-colors"
                  aria-expanded={otwarty}
                  onClick={() => setOtwarty(otwarty ? null : m.miesiac)}
                >
                  <span className="font-semibold text-plum capitalize w-32">{m.nazwa}</span>
                  <span className="text-caption text-plum-muted">{policz(m.total.days, 'dzien')}</span>
                  <span className="text-caption text-plum-muted tnum">
                    norma {formatDuration(m.total.creditMin)} / dostępne {formatDuration(m.total.availableMin)}
                  </span>
                  <span className={`ml-auto font-semibold tnum ${klasa(m.total.efficiency)}`}>
                    {proc(m.total.efficiency)}
                  </span>
                  <span aria-hidden className="text-plum-muted">{otwarty ? '▾' : '▸'}</span>
                </button>

                {otwarty && (
                  <table className="tbl border-t border-line">
                    <thead>
                      <tr>
                        <th>Dzień</th>
                        <th>Tydzień</th>
                        <th className="text-right">Rozpisane</th>
                        <th className="text-right">Dostępne</th>
                        <th className="text-right">Norma</th>
                        <th className="text-right">Luka</th>
                        <th className="text-right">Efektywność</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.dni.map((dz) => (
                        <tr key={dz.work_date}>
                          <td className="tnum">{dz.work_date}</td>
                          <td className="tnum text-plum-muted">{isoTydzien(dz.work_date)}</td>
                          <td className="text-right tnum">{formatDuration(dz.registeredMin)}</td>
                          <td className="text-right tnum">{formatDuration(dz.availableMin)}</td>
                          <td className="text-right tnum">{formatDuration(dz.creditMin)}</td>
                          <td className={`text-right tnum ${dz.gapMin > 15 ? 'text-bad' : 'text-plum-muted'}`}>
                            {formatDuration(Math.max(0, dz.gapMin))}
                          </td>
                          <td className={`text-right tnum font-semibold ${klasa(dz.efficiency)}`}>
                            {proc(dz.efficiency)}
                          </td>
                          <td className="text-right">
                            <button className="btn-quiet !min-h-0 !py-1 !px-2 text-caption"
                                    onClick={() => nav(`/praca?od=${dz.work_date}&do=${dz.work_date}&pracownik=${id}`)}>
                              Wpisy →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function isoTydzien(data: string): number {
  const d = new Date(`${data}T00:00:00Z`);
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3);
  const jan4 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((d.getTime() - jan4.getTime()) / 86400000 - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7);
}
